import React, { useEffect, useState } from 'react';
import { authHeaders } from '../../../utils/api';
import styles from './WhatsappSection.module.css';
import WhatsappRemarketingSection from './WhatsappRemarketingSection';

const API = process.env.REACT_APP_API_URL || '/api';
const dateLabel = (date) => date ? new Date(date).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Sin fecha';
const deliveryLabel = { read: 'Leído', delivered: 'Entregado', sent: 'Enviado', queued: 'En cola',
  failed: 'Fallido', undelivered: 'No entregado', sending: 'Enviando' };
const safeUrl = (value) => {
  try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; } catch { return null; }
};

export default function WhatsappSection() {
  const [search, setSearch] = useState('');
  const [dateDraft, setDateDraft] = useState({ dateFrom: '', dateTo: '' });
  const [dates, setDates] = useState({ dateFrom: '', dateTo: '' });
  const [dateError, setDateError] = useState('');
  const [filters, setFilters] = useState({ search: '', inactiveHours: '24', inactiveOnly: false, stage: '', page: 1 });
  const [revision, setRevision] = useState(0);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [messagePage, setMessagePage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError('');
    const query = new URLSearchParams({ ...filters, ...dates });
    fetch(`${API}/admin/whatsapp?${query}`, { headers: authHeaders(), signal: controller.signal, cache: 'no-store' })
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) throw new Error(body.message || 'No se pudo consultar WhatsApp.');
        if (!controller.signal.aborted) setOverview(body.data);
      }).catch((err) => { if (!controller.signal.aborted) { setError(err.message); setOverview(null); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters, dates, revision]);

  useEffect(() => {
    if (!selected) return undefined;
    const controller = new AbortController();
    setDetail(null); setDetailError(''); setDetailLoading(true);
    const query = new URLSearchParams({ page: messagePage, ...dates });
    fetch(`${API}/admin/whatsapp/${encodeURIComponent(selected._id)}?${query}`, {
      headers: authHeaders(), signal: controller.signal, cache: 'no-store',
    }).then(async (response) => {
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'No se pudo cargar la conversación.');
      if (!controller.signal.aborted) setDetail(body.data);
    }).catch((err) => { if (!controller.signal.aborted) setDetailError(err.message); })
      .finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
  }, [selected, messagePage, dates, revision]);

  const changeFilter = (values) => setFilters((current) => ({ ...current, ...values, page: 1 }));
  const applyDates = (range) => {
    if (range.dateFrom && range.dateTo && range.dateFrom > range.dateTo) {
      setDateError('La fecha Desde debe ser anterior o igual a Hasta.');
      return;
    }
    setDateError(''); setDates(range); changeFilter({}); setMessagePage(1);
  };
  const stages = overview?.stages || [];
  const total = stages.reduce((sum, stage) => sum + stage.count, 0);
  const inactive = stages.reduce((sum, stage) => sum + stage.inactive, 0);
  const maximum = Math.max(1, ...stages.map((stage) => stage.count));

  return <section className={styles.container}>
    <div className={styles.heading}><div><h2>WhatsApp</h2><p>Conversaciones de Twilio y última etapa del flujo por contacto.</p></div>
      <button type="button" onClick={() => setRevision((value) => value + 1)} disabled={loading}>Actualizar</button></div>
    <form className={styles.filters} aria-label="Fechas de WhatsApp" onSubmit={(event) => { event.preventDefault(); applyDates(dateDraft); }}>
      <label>Desde<input type="date" value={dateDraft.dateFrom} max={dateDraft.dateTo || undefined}
        onChange={(event) => setDateDraft((current) => ({ ...current, dateFrom: event.target.value }))} /></label>
      <label>Hasta<input type="date" value={dateDraft.dateTo} min={dateDraft.dateFrom || undefined}
        onChange={(event) => setDateDraft((current) => ({ ...current, dateTo: event.target.value }))} /></label>
      <button type="submit">Aplicar</button>
      <button type="button" onClick={() => { const empty = { dateFrom: '', dateTo: '' }; setDateDraft(empty); applyDates(empty); }}>Limpiar</button>
    </form>
    {dateError && <p role="alert">{dateError}</p>}
    <p className={styles.note}>Período aplicado: {dates.dateFrom || 'desde el inicio'} — {dates.dateTo || 'sin fecha final'}. Días completos de CDMX. Abarca contactos con mensajes en el período, historiales y envíos de remarketing.</p>
    <form className={styles.filters} onSubmit={(event) => { event.preventDefault(); changeFilter({ search: search.trim() }); }}>
      <label>Nombre o teléfono<input value={search} onChange={(event) => setSearch(event.target.value)} maxLength={100} placeholder="Buscar contacto" /></label>
      <label>Umbral de inactividad<select value={filters.inactiveHours} onChange={(event) => changeFilter({ inactiveHours: event.target.value })}>
        <option value="24">24 horas</option><option value="48">48 horas</option><option value="72">72 horas</option><option value="168">7 días</option>
      </select></label>
      <label className={styles.checkbox}><input type="checkbox" checked={filters.inactiveOnly} onChange={(event) => changeFilter({ inactiveOnly: event.target.checked })} />Solo sin actividad</label>
      <button type="submit">Buscar</button>
    </form>
    <p className={styles.note}>Sin actividad = sin mensajes entrantes ni salientes durante el plazo elegido. No confirma abandono. Horarios de Ciudad de México.</p>
    {error && <p role="alert">{error}</p>}
    {loading && <p role="status">Cargando WhatsApp…</p>}
    {!loading && overview && <>
      <div className={styles.metrics}><div><strong>{total}</strong> contactos</div><div><strong>{inactive}</strong> sin actividad ≥ {filters.inactiveHours} h</div></div>
      <div className={styles.card}>
        <h3>¿En qué etapa se quedan?</h3>
        <p className={styles.note}>Distribución actual de los contactos con mensajes en el período, un contacto por etapa. Selecciona una barra para filtrar la lista. La gráfica respeta las fechas, la búsqueda y el filtro de inactividad.</p>
        <div className={styles.legend}><span>■ Con actividad reciente</span><span>■ Sin actividad</span></div>
        <div role="group" aria-label="Contactos por etapa" className={styles.chart}>
          {stages.map((stage) => <button key={stage.key} type="button" aria-pressed={filters.stage === stage.key}
            aria-label={`${stage.label}: ${stage.count} contactos, ${stage.inactive} sin actividad`}
            onClick={() => changeFilter({ stage: filters.stage === stage.key ? '' : stage.key })} className={styles.barRow}>
            <span>{stage.label}</span><span className={styles.track}>
              <span className={styles.activeBar} style={{ width: `${(stage.count - stage.inactive) / maximum * 100}%` }} />
              <span className={styles.inactiveBar} style={{ width: `${stage.inactive / maximum * 100}%` }} />
            </span><strong>{stage.count}</strong>
          </button>)}
        </div>
        <p className={styles.note}>“Enlace de inscripción enviado” no significa inscripción completada. “Sin etapa registrada” incluye historiales antiguos cuya sesión expiró.</p>
      </div>
      <div className={styles.columns}>
        <div className={styles.card}>
          <div className={styles.heading}><h3>Contactos ({overview.total})</h3>{filters.stage && <button type="button" onClick={() => changeFilter({ stage: '' })}>Todas las etapas</button>}</div>
          {filters.stage && <p>Etapa: {stages.find((stage) => stage.key === filters.stage)?.label}</p>}
          {!overview.contacts.length && <p>No hay contactos con estos filtros.</p>}
          <div className={styles.contactList}>{overview.contacts.map((contact) => <button key={contact._id} type="button"
            className={styles.contact} aria-pressed={selected?._id === contact._id}
            onClick={() => { setSelected(contact); setMessagePage(1); }}>
            <strong>{contact.name || contact._id}</strong><span>{contact._id}</span>
            <span>{stages.find((stage) => stage.key === contact.stage)?.label}</span>
            <span>{contact.inactive ? 'Sin actividad' : 'Actividad reciente'} · {dateLabel(contact.lastActivityAt)}</span>
            <span className={styles.preview}>{contact.lastMessage?.text || 'Mensaje sin texto'}</span>
          </button>)}</div>
          <div className={styles.pagination}><button type="button" disabled={filters.page === 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Anterior</button>
            <span>Página {overview.page} de {Math.max(1, Math.ceil(overview.total / overview.pageSize))}</span>
            <button type="button" disabled={filters.page * overview.pageSize >= overview.total} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Siguiente</button></div>
        </div>
        <div className={styles.card} aria-label="Historial de conversación">
          <h3>{selected ? `Conversación · ${selected.name || selected._id}` : 'Historial de mensajes'}</h3>
          <p className={styles.note}>Historial guardado por la aplicación: hasta 500 mensajes por conversación. Puede estar incompleto; no es una importación del archivo de Twilio.</p>
          {!selected && <p>Selecciona un contacto para consultar sus mensajes.</p>}
          {detailLoading && <p role="status">Cargando conversación…</p>}
          {detailError && <p role="alert">{detailError}</p>}
          {detail && <>
            {!detail.total && <p>No hay mensajes en el período seleccionado.</p>}
            <div className={styles.pagination}>
              <button type="button" disabled={messagePage * detail.pageSize >= detail.total} onClick={() => setMessagePage((value) => value + 1)}>Mensajes anteriores</button>
              <span>{detail.total} mensajes en el período</span>
              <button type="button" disabled={messagePage === 1} onClick={() => setMessagePage((value) => value - 1)}>Más recientes</button>
            </div>
            <div className={styles.messages}>{detail.messages.map((message, index) => <article key={`${message.messageId}-${index}`}
              className={`${styles.message} ${message.direction === 'outgoing' ? styles.outgoing : ''}`}>
              <strong>{message.direction === 'outgoing' ? 'ODM' : detail.name || detail.phone}</strong>
              {message.metadata?.remarketing && <small>Remarketing · Toque {message.metadata.touch}</small>}
              {message.metadata?.header && <p><b>{message.metadata.header}</b></p>}
              <p>{message.text || 'Mensaje sin texto'}</p>
              {!!(message.metadata?.rows || message.metadata?.buttons)?.length && <ul>
                {(message.metadata.rows || message.metadata.buttons).map((option, optionIndex) => <li key={option.id || optionIndex}>{option.title || option.text || option.description}</li>)}
              </ul>}
              {safeUrl(message.metadata?.url) && <a href={safeUrl(message.metadata.url)} target="_blank" rel="noreferrer">Ver enlace enviado</a>}
              <small>{dateLabel(message.date)}{message.direction === 'outgoing' && ` · ${deliveryLabel[message.delivery?.currentStatus] || message.delivery?.currentStatus || 'Sin confirmación de entrega'}`}</small>
              {message.delivery?.errorCode && <small>Error {message.delivery.errorCode}: {message.delivery.errorMessage}</small>}
            </article>)}</div>
          </>}
        </div>
      </div>
    </>}
    <WhatsappRemarketingSection revision={revision} dateFrom={dates.dateFrom} dateTo={dates.dateTo} />
  </section>;
}
